import { useRef, useState } from "react";
import { useBlocker } from "react-router-dom";

export type LeaveConfirmation = {
	open: boolean;
	onSave: () => void;
	onDiscard: () => void;
	onClose: () => void;
};

type Args = {
	hasUnsavedChanges: boolean;
	isPending: boolean;
};

export const useLeaveConfirmation = ({
	hasUnsavedChanges,
	isPending,
}: Args) => {
	const isSavedRef = useRef(false);
	const [isSavingFromConfirmation, setIsSavingFromConfirmation] =
		useState(false);
	const blocker = useBlocker(
		() => !isSavedRef.current && (isPending || hasUnsavedChanges),
	);
	const blockerRef = useRef(blocker);
	blockerRef.current = blocker;

	const completeSave = (navigateAway: () => void) => {
		isSavedRef.current = true;
		if (blockerRef.current.state === "blocked") {
			blockerRef.current.proceed();
		} else {
			navigateAway();
		}
	};

	const cancelSave = () => {
		setIsSavingFromConfirmation(false);
		blockerRef.current.reset?.();
	};

	const createLeaveConfirmation = (save: () => void): LeaveConfirmation => ({
		open:
			blocker.state === "blocked" && (!isPending || isSavingFromConfirmation),
		onSave: () => {
			setIsSavingFromConfirmation(true);
			save();
		},
		onDiscard: () => blocker.proceed?.(),
		onClose: () => blocker.reset?.(),
	});

	return { completeSave, cancelSave, createLeaveConfirmation };
};
